/**
 * DiffView — the ONE review component. The resolve preview, the AI proposal, and
 * the audit before/after all emit the SAME ConfigDiff shape (seam #4), so this
 * single component renders every "review the diff, confirm" moment in the
 * product. Operators learn the gesture once; it transfers everywhere.
 *
 * This is the Phase-0 stub: it renders the canonical diff and the confirm/discard
 * gesture. It deliberately does NOT push — the caller wires onConfirm to the real
 * action (resolve+push, proposal apply) which routes through audit server-side.
 *
 * Secret values are NEVER revealed: a change flagged `secret` shows a masked
 * marker only (PSK reveal is forbidden entirely, decision #7).
 */
import type { ConfigDiff, DiffChange } from "@/types/domain";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";

interface DiffViewProps {
  diff: ConfigDiff;
  /** Confirm gesture. Destructive/mutating — gate the trigger with RoleGate and
   *  block it when offline (SyncQueue field-hazard rule). */
  onConfirm?: () => void;
  onDiscard?: () => void;
  confirmLabel?: string;
  busy?: boolean;
}

export function DiffView({
  diff,
  onConfirm,
  onDiscard,
  confirmLabel = "Confirm changes",
  busy,
}: DiffViewProps) {
  return (
    <div className="md-card overflow-hidden">
      {/* Header: summary + blast radius (the "N devices affected" line). */}
      <div className="border-b border-line px-4 py-3">
        <div className="text-sm font-medium text-ink">{diff.summary}</div>
        {diff.blast_radius && (
          <div className="mt-1 text-xs text-warn">{diff.blast_radius}</div>
        )}
      </div>

      {/* Change list — scrolls inside its own container, never the page. */}
      <ul className="max-h-96 divide-y divide-line overflow-y-auto">
        {diff.changes.map((c, i) => (
          <DiffRow key={`${c.path}-${i}`} change={c} />
        ))}
        {diff.changes.length === 0 && (
          <li className="px-4 py-6 text-center text-sm text-ink-muted">
            No differences — nothing to apply.
          </li>
        )}
      </ul>

      {/* The one confirm gesture. */}
      {(onConfirm || onDiscard) && (
        <div className="flex items-center justify-end gap-2 border-t border-line px-4 py-3">
          {onDiscard && (
            <Button variant="ghost" onClick={onDiscard} disabled={busy}>
              Discard
            </Button>
          )}
          {onConfirm && (
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={busy || diff.changes.length === 0}
            >
              {busy ? "Applying…" : confirmLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** One change line. Color encodes op via the status ramp semantics
 *  (add=healthy, remove=critical, change=warn) — this is operational state, the
 *  sanctioned use of the ramp. */
function DiffRow({ change }: { change: DiffChange }) {
  const tone =
    change.op === "add" ? "text-healthy" : change.op === "remove" ? "text-critical" : "text-warn";
  const sign = change.op === "add" ? "+" : change.op === "remove" ? "−" : "~";
  return (
    <li className="px-4 py-2 font-mono text-xs">
      <div className="flex items-baseline gap-2">
        <span className={cn("w-3 shrink-0 font-bold", tone)}>{sign}</span>
        <span className="text-ink-muted">{change.path}</span>
      </div>
      {change.op !== "add" && (
        <div className="ml-5 text-ink-subtle">
          <span className="text-ink-muted">before: </span>
          {renderValue(change.before, change.secret)}
        </div>
      )}
      {change.op !== "remove" && (
        <div className="ml-5">
          <span className="text-ink-muted">after: </span>
          {renderValue(change.after, change.secret)}
        </div>
      )}
    </li>
  );
}

/** Render a value; secrets are masked (reveal is forbidden — decision #7). */
function renderValue(value: unknown, secret?: boolean) {
  if (secret) return <span className="italic text-ink-subtle">•••••• (secret — not shown)</span>;
  if (value === undefined || value === null) return <span className="text-ink-subtle">—</span>;
  if (typeof value === "object") return <span>{JSON.stringify(value)}</span>;
  return <span>{String(value)}</span>;
}
